import ReactGA from 'react-ga';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import TagManager from 'react-gtm-module';
import { IonReactRouter } from '@ionic/react-router';
import { IonApp, IonRouterOutlet } from '@ionic/react';
import { useVersionCheck } from './hooks/useVersionCheck';

import '@ionic/react/css/core.css';
import '@ionic/react/css/display.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/float-elements.css';
// Generated by `yarn tailwind:build` from styles.css. Edit styles.css, never this file.
import '@/styles.generated.css';

import {
  DialogServiceProvider,
  SnackbarProvider,
  useDialog,
  usePrevious,
  AppErrorHandler,
} from '@ecdlink/core';

import { DialogPosition } from '@ecdlink/ui';
import { AuthRoutes, PublicRoutes } from '@/routes';
import { UpdateRequired } from '@/pages/update-required/update-required';
import InitialStoreSetup from '@/initial-store-setup';
import InitialNotificationSetup from '@/initial-notifications-setup';
import { authSelectors } from '@/store/auth';
import { settingSelectors } from '@/store/settings';
import { LoginModal } from '@/pages/auth/login-modal/login-modal';
import { stopReportingRuntimeErrors } from 'react-error-overlay';

if (process.env.NODE_ENV === 'development') {
  stopReportingRuntimeErrors();
}

function App() {
  const dialog = useDialog();
  const [updateRegistration, setUpdateRegistration] =
    useState<ServiceWorkerRegistration | null>(null);
  const user = useSelector(authSelectors.getAuthUser);
  const userExpired = useSelector(authSelectors.getUserExpired);
  const wasUserExpired = usePrevious(userExpired);
  const { updateRequired } = useVersionCheck();
  const appSettings = useSelector(settingSelectors.getApplicationSettings);

  // ---- Memoized routes ----
  const routes = useMemo(() => {
    if (user && user.isTempUser !== true) {
      return (
        <InitialStoreSetup>
          <SnackbarProvider>
            <DialogServiceProvider>
              <InitialNotificationSetup>
                <AuthRoutes />
              </InitialNotificationSetup>
            </DialogServiceProvider>
          </SnackbarProvider>
        </InitialStoreSetup>
      );
    }
    return <PublicRoutes />;
  }, [user]);

  // ---- Service worker update detection ----
  useEffect(() => {
    const handleSwUpdate = (event: Event) => {
      const { registration } = (event as CustomEvent).detail;
      setUpdateRegistration(registration);
    };
    window.addEventListener('sw-update', handleSwUpdate);
    return () => window.removeEventListener('sw-update', handleSwUpdate);
  }, []);

  // ---- Google Analytics & GTM ----
  useEffect(() => {
    const google = appSettings?.Google;
    if (!google) return;

    if (google.GoogleAnalyticsTag) {
      ReactGA.initialize(google.GoogleAnalyticsTag);
      ReactGA.pageview(window.location.pathname + window.location.search);
    }

    if (google.GoogleTagManager) {
      TagManager.initialize({ gtmId: google.GoogleTagManager });
    }
  }, [appSettings]);

  // ---- Show Login Modal if expired ----
  useEffect(() => {
    if (userExpired && userExpired !== wasUserExpired) {
      dialog({
        blocking: true,
        position: DialogPosition.Middle,
        render(onSubmit) {
          return <LoginModal loginSuccessful={onSubmit} />;
        },
      });
    }
  }, [dialog, userExpired, wasUserExpired]);

  // ---- Render ----
  if (updateRequired || updateRegistration) {
    return (
      <IonApp className="m-auto max-w-4xl bg-white">
        <UpdateRequired />
      </IonApp>
    );
  }

  return (
    <IonApp className="m-auto max-w-4xl bg-white">
      <IonReactRouter>
        {process.env.NODE_ENV === 'production' ? (
          <IonRouterOutlet>{routes}</IonRouterOutlet>
        ) : (
          <AppErrorHandler>
            <IonRouterOutlet>{routes}</IonRouterOutlet>
          </AppErrorHandler>
        )}
      </IonReactRouter>
    </IonApp>
  );
}

export default App;
